import type { API } from 'chessboard/api/config/types';
import { JCENodeHasMarking } from 'chessboard/plugins/markings/model/jce-node-has-marking';
import { removeFromJCENode } from 'chessboard/plugins/markings/model/remove-from-jce-node';
import { getMarkingsFromJCE } from 'chessboard/plugins/markings/model/get-markings-from-jce';
import { addToJCENode } from 'chessboard/plugins/markings/model/add-to-jce-node';
import type { Markings, ModifyJCEMarkingResult } from 'chessboard/plugins/markings/config/types';
import { Core } from 'chessboard/core/config/types';
import { is } from 'src/utils/app/model/is';
import { isOneOf } from 'utils/app/model/is-one-of';
import { positionChangeEvents } from 'chessboard/config/position-change-events';
import { generatePrimaryKey } from 'chessboard/plugins/markings/service';
import { getAnnotationEffectName } from 'chessboard/plugins/markings/model/get-annotation-name';
import type { Game } from 'chessboard/model/game/types';
import { nodeTargetSquare } from 'api/move/model/node-target-square';
import type JCE from '@chesscom/js-chess-engine';
import type { GameAPI } from 'chessboard/types';
import { EffectType } from '../effects/config/types';
import { parseEffects } from './model/parse-effects';

const checkSyncBefore = (handler: Function) => (event: Core.Event, context: Core.Context) => {
  if (context.api.getOptions().syncEffects) {
    handler(event, context);
  }
};

export const match = [
  { condition: isOneOf(positionChangeEvents), handler: handlePositionChange },
  { condition: is(Core.APIEvents.Mark), handler: checkSyncBefore(handleMark) },
  { condition: is(Core.APIEvents.Unmark), handler: checkSyncBefore(handleUnmark) },
  { condition: is(Core.APIEvents.Load), handler: checkSyncBefore(handleLoad) },
  { condition: is(Core.APIEvents.NodeUpdated), handler: checkSyncBefore(handleNodeUpdated) },
];

/**
 * Emit a core event when markings are added.
 */
function handleMark(
  event: Core.Event,
  context: Core.Context | API.Context,
  { includeAlternateNodes }: { includeAlternateNodes?: boolean } = {},
) {
  const { data } = event;
  const { game } = context.api.getContext();
  const addedMarkings = Array.isArray(data) ? data : [data];

  const updates: ModifyJCEMarkingResult[] = [];

  addedMarkings.forEach((marking) => {
    if (
      marking.node &&
      !JCENodeHasMarking(game, marking, { onlyCurrentNode: !includeAlternateNodes })
    ) {
      updates.push(addToJCENode(game, marking, context.api));
    }
  });

  emitBatchedNodeUpdatedMarkingEvents(updates, context.api);
}

/**
 * Emit a core event when markings are removed.
 */
function handleUnmark(
  event: Core.Event,
  context: Core.Context | API.Context,
  { includeAlternateNodes }: { includeAlternateNodes?: boolean } = {},
) {
  const { data } = event;
  const { game } = context.api.getContext();
  const removedMarkings = Array.isArray(data) ? data : [data];

  const updates: ModifyJCEMarkingResult[] = [];
  removedMarkings.forEach((marking) => {
    if (
      marking.node &&
      JCENodeHasMarking(game, marking, { onlyCurrentNode: !includeAlternateNodes })
    ) {
      updates.push(removeFromJCENode(game, marking, context.api));
    }
  });

  emitBatchedNodeUpdatedMarkingEvents(updates, context.api);
}

/**
 * Emit NodeUpdate events
 */
export function emitBatchedNodeUpdatedMarkingEvents(
  updates: ModifyJCEMarkingResult[],
  api: GameAPI | API.Extended,
) {
  if (!updates.length) {
    return;
  }

  const batchedUpdates = updates.reduce((acc, update) => {
    const key = `${update.ids.line}-${update.ids.move}`;

    if (!acc[key]) {
      const node = api.getPosition(update.ids.move, update.ids.line);
      const commands =
        node.ids.move === -1
          ? api.getRawLines()[node.ids.line]?.commands
          : (node as JCE.Node.Model).commands;

      acc[key] = {
        changes: { commands: { ...commands } },
        ids: update.ids,
        node,
        parsedCommandChanges: [],
      };
    }

    acc[key].parsedCommandChanges.push(update.changes);

    return acc;
  }, {});

  Object.values(batchedUpdates).forEach((update) => {
    api.getContext().respond(Core.APIEvents.NodeUpdated, update);
  });
}

/**
 * Handle a position change in the game.
 */
function handlePositionChange(event: Core.Event, context: Core.Context) {
  const { api, queue } = context;

  if (event.data.plyDiff === 0 && event.data.lineDiff === 0) {
    // position not changed, no need to refresh markings
    return;
  }

  queue.add(() => refreshMarkings(api));
}

/**
 * Handle load (to sync effects with annotations)
 */
function handleLoad(_event: Core.Event, context: Core.Context) {
  const { api } = context;
  const lines = api.getRawLines();
  const markings: Markings.Model[] = [];

  refreshMarkings(api);

  // Sync Effects Logic: Adds effect markings for all nodes in lines.
  lines.forEach((nodes) => {
    nodes.forEach((node) => {
      const correspondingEffect = getAnnotationEffectName(node.annotation);

      if (correspondingEffect) {
        const effect = getEffect(node, api.getContext().game);

        markings.push({
          data: effect,
          type: EffectType,
          node: node.ids,
          persistent: true,
        });
      }
    });
  });

  api.markings.addMany(markings);
}

/**
 * Handle NodeUpdated event (to sync effects with annotations).
 */
function handleNodeUpdated(event: Core.Event, context: Core.Context) {
  const { api } = context;
  const { ids, changes } = event.data;

  if (!('annotation' in changes) || changes.isSync) {
    return;
  }

  const position = api.getPosition(ids.move, ids.line);

  if (!('to' in position)) {
    return;
  }
  const correspondingEffect = getAnnotationEffectName(changes.annotation);
  const updatedNode = <Game.Move.Node>api.getNodeByIds(ids);
  const effect = getEffect(updatedNode, api.getContext().game);

  if (!correspondingEffect) {
    const key = generatePrimaryKey({ data: { square: position.to }, type: EffectType });
    api.markings.removeOne(key, ids);

    return;
  }

  api.markings.addOne({
    data: { ...effect, type: correspondingEffect },
    type: EffectType,
    node: ids,
    persistent: true,
  });
}

export { handleMark, handleUnmark };

/**
 * Refresh markings whenever new position is set.
 * @param api
 * */
function refreshMarkings(api) {
  const { game } = api.getContext();
  const markingsToAdd = getMarkingsFromJCE(game);

  // Clear all node markings from the previous position.
  api.markings.removeAllWhere({ node: true });

  if (markingsToAdd.length) {
    // Add node markings for the new position (JCE is the source of truth for node markings).
    api.markings.addMany(markingsToAdd);
  }
}

/**
 * Get effect from commands or determine effect from annotation.
 * @param node
 * */
function getEffect(node: JCE.Node.Model, game: JCE.Game) {
  const { move, line } = node.ids;
  const square = nodeTargetSquare(node);
  const markings = game.markings(move, line);
  const effectFromCommands = parseEffects(markings.effect, square);
  const correspondingEffect = getAnnotationEffectName(node.annotation);
  const effectFromAnnotation = {
    square: nodeTargetSquare(node),
    type: correspondingEffect,
  };

  const effect = effectFromCommands || effectFromAnnotation;

  return effect;
}
